/**
 * @file WmsBinsPage.tsx
 * @description Справочник ячеек адресного хранения (phase19.14, W14; общий
 * стиль, сводки и «Положить товар» — phase19.23, A4).
 *
 * Список по складу (адрес, подпись, содержимое), создание одной ячейки,
 * генератор стеллажа с превью, архивация, шторка «Содержимое ячейки» и диалог
 * «Положить товар».
 *
 * Архивные скрыты по умолчанию: код ячейки не переиспользуется, поэтому архив
 * со временем распухает и мешает работе. Данные и операции — в `useBinsPage`,
 * страница отвечает за раскладку, диалоги и sticky action-bar на mobile.
 *
 * phase19.22 шаг 5 — печать этикеток ячеек, полок и стеллажей: галочки,
 * «Стеллаж A» целиком, полоса «Выбрано N · Печать» и история печати
 * (`useBinLabels` + `BinLabelsLayer`).
 *
 * phase19.31 (зона «Склад», минимум по SC50 — экран перестроит phase19.25):
 * заголовок «Ячейки» в верхней панели, главное действие «+ Ячейка»,
 * «Сгенерировать стеллаж» — второстепенное (на телефоне — в «⋯»); своя
 * липкая полоса «Стеллаж / Ячейка» убрана — главное действие в общей нижней
 * полосе каркаса. Пока выбраны ячейки под печать, нижнюю полосу занимает
 * «Выбрано N · Печать» — главное действие на это время уходит из полосы.
 */

import { Grid3x3, Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { BinContentsSheet } from "../../../components/wms/bins/BinContentsSheet";
import { BinCreateDialog } from "../../../components/wms/bins/BinCreateDialog";
import { BinGenerateDialog } from "../../../components/wms/bins/BinGenerateDialog";
import { BinLabelsLayer } from "../../../components/wms/bins/BinLabelsLayer";
import { BinLevelScanBar } from "../../../components/wms/bins/BinLevelScanBar";
import { BinRackSelectBar } from "../../../components/wms/bins/BinRackSelectBar";
import { BinsFilterBar } from "../../../components/wms/bins/BinsFilterBar";
import { BinsList } from "../../../components/wms/bins/BinsList";
import { BinSelectionBar } from "../../../components/wms/bins/BinSelectionBar";
import { BinsOnboardingCard } from "../../../components/wms/bins/BinsOnboardingCard";
import { PutStockDialog } from "../../../components/wms/bins/PutStockDialog";
import { useBinLabels } from "../../../components/wms/bins/useBinLabels";
import { useBinsPage } from "../../../components/wms/bins/useBinsPage";
import { WmsConfirmDialog } from "../../../components/wms/WmsConfirmDialog";
import type { StorageBinDto } from "../../../lib/api-bins";
import { WAREHOUSE_KIND_META } from "../../../lib/wms-warehouse-kind";

export function WmsBinsPage() {
  const page = useBinsPage();
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [viewing, setViewing] = useState<StorageBinDto | null>(null);
  const [putting, setPutting] = useState<StorageBinDto | null>(null);
  const [search, setSearch] = useState("");
  const { warehouse, supportsBins } = page;
  const actionsDisabled = !page.warehouseId || !supportsBins;
  const labels = useBinLabels(page.bins, page.warehouseId, supportsBins);
  const selection = labels.selection;
  const compact = useMediaQuery(BELOW_LG_QUERY);
  // Идёт выбор под печать — на телефоне нижнюю полосу занимает он.
  const selecting = !!selection && selection.count > 0;

  /** Поиск по адресу и подписи — список стеллажа бывает на сотни ячеек. */
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return page.bins;
    return page.bins.filter(
      (bin) =>
        bin.code.toLowerCase().includes(q) ||
        (bin.label ?? "").toLowerCase().includes(q),
    );
  }, [page.bins, search]);


  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Ячейки"
        subtitle="Адреса хранения на складе и печать их этикеток"
        secondary={[
          { key: "generate", label: "Сгенерировать стеллаж", icon: Grid3x3, disabled: actionsDisabled, run: () => setGenerating(true) },
        ]}
        action={
          compact && selecting ? undefined : (
            <Button variant="brand" onClick={() => setCreating(true)} disabled={actionsDisabled}>
              <Plus className="size-4" />
              Ячейка
            </Button>
          )
        }
      />

      <BinsOnboardingCard />

      <BinsFilterBar
        search={search}
        onSearchChange={setSearch}
        warehouses={page.warehouses}
        warehouseId={page.warehouseId}
        onWarehouseChange={page.setWarehouseId}
        includeArchived={page.includeArchived}
        onIncludeArchivedChange={page.setIncludeArchived}
      />

      {/* phase19.22 (W14-9): скан ячейки, полки или стеллажа — «что тут лежит». */}
      {page.warehouseId && supportsBins ? (
        <BinLevelScanBar
          warehouseId={page.warehouseId}
          onOpenBin={(binId) => setViewing(page.bins.find((b) => b.id === binId) ?? null)}
        />
      ) : null}

      {selection ? (
        <BinRackSelectBar
          selection={selection}
          historyCount={labels.jobs.jobs.length}
          onOpenHistory={() => labels.setHistoryOpen(true)}
        />
      ) : null}

      <BinsList
        bins={visible}
        loading={page.loading}
        error={page.error}
        searching={!!search}
        unsupportedKindLabel={
          warehouse && !supportsBins ? WAREHOUSE_KIND_META[warehouse.kind].label : null
        }
        generateDisabled={actionsDisabled}
        onGenerate={() => setGenerating(true)}
        onOpen={setViewing}
        onPut={setPutting}
        onArchive={(bin) => void page.archive(bin)}
        onRestore={(bin) => void page.restore(bin)}
        selection={selection}
      />

      {/* Выбор под печать — своя полоса «Выбрано N · Печать». */}
      {selection && selecting ? (
        <BinSelectionBar
          count={selection.count}
          scopeNote={selection.scope.rack ? `Стеллаж ${selection.scope.rack} целиком` : null}
          onClear={selection.clear}
          onPrint={labels.openPrint}
        />
      ) : null}

      {creating && warehouse ? (
        <BinCreateDialog
          warehouseName={warehouse.name}
          saving={page.saving}
          error={page.createError}
          onSubmit={(input) => {
            void page.create(input).then((ok) => {
              if (ok) setCreating(false);
            });
          }}
          onClose={() => {
            setCreating(false);
            page.setCreateError(null);
          }}
        />
      ) : null}

      {generating && warehouse ? (
        <BinGenerateDialog
          warehouseId={warehouse.id}
          warehouseName={warehouse.name}
          onCreated={() => void page.refresh()}
          onClose={() => setGenerating(false)}
        />
      ) : null}

      {putting ? (
        <PutStockDialog
          bin={putting}
          onPlaced={() => void page.refresh()}
          onClose={() => setPutting(null)}
        />
      ) : null}

      <BinLabelsLayer labels={labels} warehouseId={page.warehouseId} warehouseName={warehouse?.name} />

      {viewing ? (
        <BinContentsSheet
          bin={viewing}
          onChanged={() => void page.refresh()}
          onClose={() => setViewing(null)}
        />
      ) : null}
      <WmsConfirmDialog request={page.confirmRequest} onSettle={page.settleConfirm} />
    </div>
  );
}
